import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { glass, ink } from '../../lib/theme';

/**
 * Crosshair sincronizado (PRD §9-D): Tendência e Contraction vs MRR Pendente são
 * indexados pela MESMA linha do tempo. Mover o mouse sobre um desenha a linha de
 * referência nos dois ao mesmo tempo — é isso que deixa explícito "nesse período
 * a contração subiu e o MRR ativo caiu junto" sem precisar de tabela.
 */
const CrosshairCtx = createContext<{ bucket: string | null; set: (b: string | null) => void }>({
  bucket: null, set: () => {},
});

export function CrosshairProvider({ children }: { children: ReactNode }) {
  const [bucket, setBucket] = useState<string | null>(null);
  const value = useMemo(() => ({ bucket, set: setBucket }), [bucket]);
  return <CrosshairCtx.Provider value={value}>{children}</CrosshairCtx.Provider>;
}

export const useCrosshair = () => useContext(CrosshairCtx);

/** Tooltip padrão dos gráficos — vidro escuro, tabular-nums, mesma família visual. */
export function ChartTooltip({ label, rows }: { label: string; rows: Array<{ name: string; value: string; color: string }> }) {
  return (
    <div style={{ ...glass, borderRadius: 12, padding: '10px 13px', minWidth: 170 }}>
      <div style={{
        fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: ink(0.5), marginBottom: 7,
      }}>{label}</div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flex: 'none' }} />
          <span style={{ fontSize: 12, color: ink(0.65), flex: 1 }}>{r.name}</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: ink(0.95), fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

export function Legend({ items }: { items: Array<{ label: string; color: string; dashed?: boolean }> }) {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: ink(0.5) }}>
          <span style={{
            width: 10, height: 10, borderRadius: 3, flex: 'none',
            background: it.dashed ? 'transparent' : it.color,
            border: it.dashed ? `1.5px dashed ${it.color}` : 'none',
          }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/** Rótulo curto do bucket de data ("ago/26", "12/08") conforme a granularidade. */
export function bucketLabel(bucket: string, granularity: string): string {
  const d = new Date(bucket);
  if (Number.isNaN(d.getTime())) return bucket;
  if (granularity === 'month') return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

export const axisStyle = { fontSize: 10.5, fill: ink(0.34) } as const;
export const gridStroke = ink(0.06);

export function ChartEmpty({ text = 'Sem dado no período filtrado.' }: { text?: string }) {
  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12.5, color: ink(0.32), textAlign: 'center', padding: 20,
    }}>{text}</div>
  );
}
